import { useEffect, useMemo, useState } from 'react';
import type { PricingProvider, ProductModel, QuoteResult, ViewMetric } from '@ox/schema';
import { useEditorStore } from '../store/editor-store';
import { getUsedViewMetrics } from '../lib/viewMetrics';
import { printServiceQuoteFields, modelQuoteFields } from '../lib/printServiceQuote';
import { purchasableColorKey } from '../lib/modelAxes';

function pickVariant(
  model: ProductModel,
  colorKey: string,
  sizeId?: string,
  colorHex?: string,
) {
  const buyKey = purchasableColorKey(model, colorKey, colorHex);
  const forColor = model.variants.filter((v) => v.colorKey === buyKey);
  if (sizeId) {
    const match = forColor.find((v) => v.sizeId === sizeId && v.availability !== 'out_of_stock');
    if (match) return match;
  }
  const inStock = forColor.find((v) => v.availability !== 'out_of_stock');
  if (inStock) return inStock;
  return forColor[0] ?? model.variants[0] ?? null;
}

function formatPrice(currency: string, amount: number): string {
  return `${currency} ${amount.toFixed(2)}`;
}

/**
 * Live unit price (qty 1) for the current color + design footprint.
 * Debounced; falls back to catalog priceFrom while loading / on error.
 */
export function useLiveQuote(pricing: PricingProvider, designId: string) {
  const model = useEditorStore((s) => s.model);
  const document = useEditorStore((s) => s.document);
  const colorKey = document.selection.colorKey;
  const colorHex = document.selection.colorHex;
  const sizeId = document.selection.sizeId;
  const garmentPrintType = document.selection.garmentPrintType;
  const franelaReactiva = document.selection.franelaReactiva;
  const printMethod = model?.printMethod;

  const viewMetrics = useMemo(
    () => getUsedViewMetrics(document, model?.printAreas),
    [document, model?.printAreas],
  );
  const metricsKey = useMemo(() => JSON.stringify(viewMetrics), [viewMetrics]);
  const printOptsKey = `${garmentPrintType ?? ''}|${franelaReactiva ? '1' : '0'}|${printMethod ?? ''}|${model?.id ?? ''}`;

  const variant = useMemo(
    () => (model ? pickVariant(model, colorKey, sizeId, colorHex) : null),
    [model, colorKey, sizeId, colorHex],
  );

  const [quote, setQuote] = useState<QuoteResult | null>(null);

  useEffect(() => {
    if (!model || !variant) {
      setQuote(null);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(() => {
      void pricing
        .quote({
          lines: [{ variantId: variant.id, quantity: 1 }],
          designId: designId && designId !== 'draft' ? designId : undefined,
          viewMetrics,
          ...printServiceQuoteFields(document.selection),
          ...modelQuoteFields(model),
        })
        .then((q) => {
          if (!cancelled && q?.total) setQuote(q);
        })
        .catch(() => {
          /* keep last good quote / fallback label */
        });
    }, 280);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [pricing, designId, variant, metricsKey, model, viewMetrics, printOptsKey, document.selection]);

  const fallbackLabel = model
    ? formatPrice(model.priceFrom.currency, model.priceFrom.amount)
    : '—';

  const label = quote?.total
    ? formatPrice(quote.total.currency, quote.total.amount)
    : fallbackLabel;

  const hasPrintExtras = (quote?.breakdown ?? []).some((b) => !b.included && b.amount > 0);

  return {
    quote,
    label,
    viewMetrics: viewMetrics as ViewMetric[],
    hasPrintExtras,
    colorKey,
  };
}
